import Home from '@mui/icons-material/Home';

import config from '../config';
import NavigationLink from '../interfaces/NavigationLink';
import Permissions from '../shared/enums/Permissions';
import CustomerCube from '../shared/icons/CustomerCube';
import ImportExport from '../shared/icons/ImportExport';
import Intelliclose from '../shared/icons/Intelliclose';
import Offerio from '../shared/icons/Offerio';
import GiftVoucher from '../shared/icons/GiftVoucher';
import Reports from '../shared/icons/Reports';
import Retensee from '../shared/icons/Retensee';
import SocialMediaReward from '../shared/icons/SocialMediaReward';
import CommerceConnect from '../shared/icons/CommerceConnect';
import PollIcon from '@mui/icons-material/Poll';
import FlipSell from "../shared/icons/FlipSell";
import DigitalReceipt from '../shared/icons/DigitalReceipt';
import JourneyBuilder from '../shared/icons/JourneyBuilder';
import TicketDesk from '../shared/icons/TicketDesk';
import SummarizeIcon from '@mui/icons-material/Summarize';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RedeemIcon from '@mui/icons-material/Redeem';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import { Settings } from '@mui/icons-material';


const { dashboardClassic, supersetUrl, systemPage } = config;

const navConfig: NavigationLink[] = [
  {
    requiredPermissions: [],
    label: 'Home',
    content: `/`,
    externalLink: false,
    icon: Home,
  },
  {
    label: 'Customer 360',
    icon: PersonSearchIcon,
    requiredPermissions: [Permissions.CUSTOMER_360],
    content: `/customer-cube/customer-360`,
    externalLink: false,
  },
  {
    requiredPermissions: [],
    label: 'Customer Insights',
    icon: CustomerCube,
    isNew: false,
    content: [
      {
        content: `/customer-cube/customer-analysis`,
        label: 'Customer Analysis',
        requiredPermissions: [Permissions.CUSTOMER_ANALYSIS],
        externalLink: false,
      },
      {
        content: `/customer-cube/store-analysis`,
        label: 'Store Analysis',
        requiredPermissions: [Permissions.STORE_ANALYSIS],
        externalLink: false,
      },
      {
        label: "Executive Summary",
        icon: SummarizeIcon,
        requiredPermissions: [Permissions.CUSTOMER_CUBE_MANAGE],
        content: `/executive-summary`,
        externalLink: false,
        isNew: false,
      },
      {
        content: `/customer-cube/store-sales-forecast`,
        label: 'Store Sales Prediction',
        requiredPermissions: [
          Permissions.STORE_ANALYSIS,
          Permissions.STORE_SALES_PREDICTION,
        ],
        externalLink: false,
      },
      {
        content: `/customer-cube/festival-analysis`,
        label: 'Festival Analysis',
        requiredPermissions: [Permissions.FESTIVAL_ANALYSIS],
        externalLink: false,
      },
      {
        label: 'Product',
        requiredPermissions: [],
        externalLink: false,
        content: [
          {
            content: `/customer-cube/product-analysis`,
            label: 'Product Analysis',
            requiredPermissions: [Permissions.PRODUCT_ANALYSIS],
            externalLink: false,
          },
          {
            content: `/customer-cube/product-frequency`,
            label: 'Product Frequency',
            requiredPermissions: [Permissions.PRODUCT_FREQUENCY],
            externalLink: false,
          },
          {
            content: `/customer-cube/basket-analysis`,
            label: "Basket Analysis",
            requiredPermissions: [Permissions.BASKET_ANALYSIS],
            externalLink: false,
          },
        ],
      },
      {
        content: `/migrate-customers`,
        label: 'Migrate Customer',
        requiredPermissions: [Permissions.MIGRATE_CUSTOMER],
        externalLink: false,
      },

    ],
    externalLink: false,
  },
  {
    requiredPermissions: [Permissions.LEAD_CREATE],
    label: 'Lead Management',
    icon: Intelliclose,
    content: [
      {
        requiredPermissions: [Permissions.LEAD_CREATE],
        content: `/leads/create`,
        label: 'Create Lead',
        externalLink: false,
      },
      {
        content: `/leads`,
        label: 'Manage Leads',
        requiredPermissions: [Permissions.LEAD_MANAGE],
        externalLink: false,
      },
      {
        requiredPermissions: [Permissions.LEAD_DASHBOARD],
        content: `/lead-dashboard`,
        label: 'Lead Dashboard',
        externalLink: false,
      },
      {
        requiredPermissions: [Permissions.LEAD_MANAGE],
        content: `/leads/telecall-history`,
        label: 'Telecall / Prospect',
        externalLink: false,
      },
    ],
    externalLink: false,
  },
  {
    label: 'Journey Builder',
    icon: JourneyBuilder,
    requiredPermissions: [],
    externalLink: false,
    content: [
      /*
       * FLAT ON PURPOSE — one level, no sub-headings.
       *
       * The duplicate-"Analytics" problem this group had is fixed by the LABELS, not by nesting:
       * `/campaign-analysis` is now "Reports", so it no longer competes with "Analytics" for the
       * same word. Nesting would have solved the same problem at the cost of an extra click on
       * every item.
       */
      {
        label: 'Manage Journeys',
        content: `/journeys`,
        requiredPermissions: [Permissions.JOURNEY_BUILDER_MANAGE],
        externalLink: false,
      },
      {
        label: 'Analytics',
        content: `/journeys/analytics`,
        requiredPermissions: [Permissions.JOURNEY_BUILDER_MANAGE],
        externalLink: false,
      },
      {
        label: 'Create Cohort',
        content: `${dashboardClassic}/create-cohorts`,
        requiredPermissions: [Permissions.CAMPAIGN_MANAGE],
        externalLink: true,
      },
      {
        label: 'List Cohorts',
        content: `cohorts`,
        requiredPermissions: [Permissions.CAMPAIGN_MANAGE],
        externalLink: false,
      },
      {
        label: 'Manage Campaigns',
        content: `/campaign/list`,
        requiredPermissions: ['campaign_manage'],
        externalLink: false,
      },
      {
        // Was "Campaign Analytics", which read as a second analytics page beside the one above.
        // See docs/analytics-and-reports-navigation.md.
        label: 'Reports',
        content: `/campaign-analysis`,
        requiredPermissions: [Permissions.CAMPAIGN_MANAGE],
        externalLink: false,
      },
      // {
      //   label: 'Copy Write Templates',
      //   content: `/campaign/copy-write/templates`,
      //   requiredPermissions: [Permissions.COPY_WRITE],
      //   externalLink: false,
      // },
      // {
      //   label: 'Email Templates',
      //   content: [
      //     {
      //       label: 'Create',
      //       content: `${dashboardClassic}/email-templates/create`,
      //       requiredPermissions: [Permissions.CAMPAIGN_MANAGE],
      //       externalLink: true,
      //     },
      //     {
      //       label: 'List',
      //       content: `${dashboardClassic}/email-templates/list`,
      //       requiredPermissions: [Permissions.CAMPAIGN_MANAGE],
      //       externalLink: true,
      //     },
      //   ],
      //   requiredPermissions: [Permissions.CAMPAIGN_MANAGE],
      //   externalLink: true,
      // },
      {
        label: ' Templates',
        content: '/campaign/templates/sms',
        requiredPermissions: [Permissions.CAMPAIGN_MANAGE],
        externalLink: false,
      },
    ],
  },
  {
    label: 'Ticket Desk',
    icon: TicketDesk,
    requiredPermissions: [Permissions.TICKET_MANAGEMENT],
    content: '/tickets',
    externalLink: false,
    isNew: true,
  },
  {
    requiredPermissions: [Permissions.OFFERS_MANAGEMENT],
    label: 'Coupon Management',
    icon: Offerio,
    content: [
      {
        content: `offers/create`,
        label: "Create Offer",
        requiredPermissions: [Permissions.OFFERS_MANAGEMENT],
        externalLink: false,
      },
      {
        content: `offers`,
        label: "Offers List",
        requiredPermissions: [Permissions.OFFERS_MANAGEMENT],
        externalLink: false,
      },
      {
        content: `customer-offers`,
        label: 'Customer Offers & Aliases',
        requiredPermissions: [Permissions.OFFERS_MANAGEMENT],
        externalLink: false,
        isNew: true,
      },
      {
        content: `${dashboardClassic}/offers/search`,
        label: 'Customer Offers Search',
        requiredPermissions: [Permissions.OFFERS_MANAGEMENT],
        externalLink: true,
      },
      {
        content: `${dashboardClassic}/offers-analytics`,
        label: 'Offers Analytics',
        requiredPermissions: [Permissions.OFFERS_MANAGEMENT],
        externalLink: true,
      },
    ],
    externalLink: false,
  },
  {
    requiredPermissions: [Permissions.LOYALTY],
    label: 'Loyalty Management',
    content: [
      {
        requiredPermissions: [Permissions.LOYALTY],
        label: 'Adhoc Points',
        content: `/loyalty-adhoc-points`,
        externalLink: false,
      },
    ],
    externalLink: false,
    icon: Retensee,
  },
  {
    requiredPermissions: [Permissions.VOUCHER_MANAGEMENT],
    label: 'Voucher Management',
    icon: GiftVoucher,
    content: [
      {
        content: `gift-vouchers/create`,
        label: "Create Gift Voucher",
        requiredPermissions: [Permissions.CREATE_VOUCHER],
        externalLink: false,
      },
      {
        content: `gift-vouchers`,
        label: "Gift Vouchers List",
        requiredPermissions: [Permissions.VIEW_VOUCHER],
        externalLink: false,
      },
      {
        content: `${dashboardClassic}/gift-vouchers/search`,
        label: 'Customer Voucher Search',
        requiredPermissions: [Permissions.VOUCHER_MANAGEMENT],
        externalLink: true,
      },
    ],
    externalLink: false,
  },
  {
    label: 'E Commerce',
    icon: StorefrontIcon,
    requiredPermissions: [],
    externalLink: false,
    content: [
      {
        label: 'Social Media Campaign',
        content: `/social-media-campaign`,
        requiredPermissions: [Permissions.SOCIAL_MEDIA],
        externalLink: false,
      },
      {
        content: `/omni-channel-dashboard`,
        label: "Digital Dashboard",
        requiredPermissions: [Permissions.OMNI_CHANNEL],
        externalLink: false,
      },
      {
        content: `/online-behaviour`,
        label: "Online Behaviour",
        requiredPermissions: [Permissions.OMNI_CHANNEL],
        externalLink: false,
      },
      {
        content: `/abandonment-intelligence`,
        label: "Abandonment Intelligence",
        requiredPermissions: [Permissions.OMNI_CHANNEL],
        externalLink: false,
      },
      {
        content: `/ad-attribution`,
        label: "Ad Attribution",
        requiredPermissions: [Permissions.OMNI_CHANNEL],
        externalLink: false,
      },
      {
        content: `/multi-touch`,
        label: "Multi-Touch",
        requiredPermissions: [Permissions.OMNI_CHANNEL],
        externalLink: false,
      },
    ],
  },
  {
    requiredPermissions: [Permissions.VIEW_FLIPBOOK],
    label: 'FlipSell',
    content: '/flipsell',
    externalLink: false,
    isNew: true,
    icon: FlipSell,
  },
  {
    requiredPermissions: [Permissions.INVOICE_CREATOR],
    label: 'Digital Receipt',
    content: '/invoice-creator',
    externalLink: false,
    icon: DigitalReceipt,
  },
  {
    label: 'Scheme Management',
    icon: RedeemIcon,
    requiredPermissions: [Permissions.SCHEME_MANAGEMENT],
    externalLink: false,
    content: [
      {
        label: 'Management',
        content: '/scheme-management',
        requiredPermissions: [Permissions.SCHEME_MANAGEMENT],
        externalLink: false,
      },
      {
        label: 'Extend Validity',
        content: '/scheme-management/extend-validity',
        requiredPermissions: [Permissions.SCHEME_MANAGEMENT],
        externalLink: false,
      },
    ],
  },
  {
    requiredPermissions: [Permissions.DIGITAL_RECEIPT_V2],
    label: 'Digital Receipt V2',
    content: '/invoice-creator-v2',
    externalLink: false,
    isNew: true,
    icon: DigitalReceipt,
  },

  {
    requiredPermissions: [Permissions.SURVEY],
    label: 'Survey',
    icon: PollIcon,
    content: [
      {
        requiredPermissions: [Permissions.SURVEY],
        label: 'Manage Survey',
        content: '/survey/list',
        externalLink: false,
      },
      {
        requiredPermissions: [Permissions.SURVEY],
        label: 'Analytics',
        content: '/survey/dashboard',
        externalLink: false,
      },
    ],
    externalLink: false,
    isNew: false,
  },
  {
    label: 'Commerce Connect',
    icon: CommerceConnect,
    requiredPermissions: [Permissions.COMMERCE_CONNECT],
    content: [
      {
        requiredPermissions: [Permissions.COMMERCE_CONNECT],
        content: `/commerce-connect/product-attributes`,
        label: 'Product Attributes',
        externalLink: false,
      },
      {
        requiredPermissions: [Permissions.COMMERCE_CONNECT],
        content: `/commerce-connect/upload-products`,
        label: 'Upload Products',
        externalLink: false,
      },
    ],
    externalLink: false,
  },
  {
    label: 'Reports',
    icon: Reports,
    requiredPermissions: [],
    content: [
      // Superset opens in a new tab (SidebarListItem appends ?bu=<id>&ref=v2);
      // shown only to users holding the `superset` permission.
      ...(supersetUrl
        ? [
            {
              label: 'Superset',
              requiredPermissions: [Permissions.SUPERSET],
              content: supersetUrl,
              externalLink: true,
            },
          ]
        : []),
      {
        label: 'Loyalty',
        content: [
          // {
          //   label: 'Tier Downgrade',
          //   content: `${dashboardClassic}/reports/tier-downgrade-report`,
          //   requiredPermissions: [Permissions.TIER_DOWNGRADE_REPORT],
          //   externalLink: true,
          // },
          {
            label: 'Liability',
            content: `reports/loyalty-liability`,
            requiredPermissions: [Permissions.LOYALTY_LIABILITY_REPORT],
            externalLink: false,
          },
        ],
        requiredPermissions: [],
        externalLink: true,
      },
      {
        label: 'Member',
        content: [
          {
            label: 'Transaction',
            content: `reports/transaction`,
            requiredPermissions: [Permissions.TRANSACTION_REPORT],
            externalLink: false,
          },
          {
            label: 'Enrollment',
            content: `reports/enrollment`,
            requiredPermissions: [Permissions.MEMBER_BILL_REPORT],
            externalLink: false,
          },
          {
            label: 'Fraud',
            content: `reports/fraud`,
            requiredPermissions: [Permissions.FRAUD_REPORT],
            externalLink: false,
          },
        ],
        requiredPermissions: [],
        externalLink: false,
      },
      {
        label: 'Lead',
        content: [
          {
            label: 'Overall Lead Info',
            content: `reports/leads/overall-lead`,
            requiredPermissions: [Permissions.OVERALL_LEAD_REPORT],
            externalLink: false,
          },
          {
            label: 'Store Category',
            content: `reports/leads/store-category`,
            requiredPermissions: [Permissions.STORE_CATEGORY_REPORT],
            externalLink: false,
          },
          {
            label: 'Lead History',
            content: `reports/leads/lead-history`,
            requiredPermissions: [Permissions.LEAD_HISTORY_REPORT],
            externalLink: false,
          },
          {
            label: 'Source Efficiency',
            content: `reports/leads/source-efficiency`,
            requiredPermissions: [Permissions.SOURCE_EFFICIENCY_REPORT],
            externalLink: false,
          },
          {
            label: 'Store Efficiency',
            content: `reports/leads/store-efficiency`,
            requiredPermissions: [Permissions.STORE_EFFICIENCY_REPORT],
            externalLink: false,
          },
          {
            label: 'Store Person Efficiency',
            content: `reports/leads/store-person-efficiency`,
            requiredPermissions: [Permissions.STORE_PERSON_EFFICIENCY_REPORT],
            externalLink: false,
          },
          {
            label: 'Product Hierarchy Efficiency',
            content: `reports/leads/product-efficiency`,
            requiredPermissions: [
              Permissions.PRODUCT_HIERARCHY_EFFICIENCY_REPORT,
            ],
            externalLink: false,
          },
          {
            label: 'Lead Telecall Report',
            content: `reports/leads/lead-telecall`,
            requiredPermissions: [
              Permissions.LEAD_TELECALL_REPORT,
            ],
            externalLink: false,
          },
          {
            // Same permission as the Overall Lead Report: the same leads, with remarks on what to fix.
            label: 'Lead Data Quality Report',
            content: `reports/leads/data-quality`,
            requiredPermissions: [Permissions.OVERALL_LEAD_REPORT],
            externalLink: false,
          },
        ],
        requiredPermissions: [Permissions.LEAD_CSV_DOWNLOAD],
        externalLink: false,
      },
      // {
      //   label: "Campaign",
      //   content: `reports/campaign`,
      //   requiredPermissions: [Permissions.CAMPAIGN_TENANT_REPORT],
      //   externalLink: false,
      // },
      {
        label: 'KPI',
        content: `reports/kpi`,
        requiredPermissions: [Permissions.KPI_REPORT],
        externalLink: false,
      },
      {
        label: 'Offer',
        requiredPermissions: [Permissions.OFFER_TENANT_REPORT],
        content: `reports/offer`,
        externalLink: false,
      },
      {
        label: 'Offer Redemption',
        requiredPermissions: [Permissions.OFFER_TENANT_REPORT],
        content: `reports/offer-redemption`,
        externalLink: false,
      },
      {
        label: 'Offer Earn Report',
        requiredPermissions: [Permissions.OFFER_EARN_REPORT],
        content: `reports/offer-earn`,
        externalLink: false,
      },
      {
        label: 'Voucher',
        requiredPermissions: [Permissions.VOUCHER_MANAGEMENT],
        content: `reports/voucher`,
        externalLink: false,
      },
      {
        label: 'Scheme',
        requiredPermissions: [Permissions.SCHEME_MANAGEMENT],
        content: `reports/scheme`,
        externalLink: false,
      },
      {
        label: 'Store Health',
        requiredPermissions: [Permissions.STORE_HEALTH_REPORT],
        content: `reports/store-health`,
        externalLink: false,
      },
      {
        label: 'Survey',
        content: `/reports/survey`,
        requiredPermissions: [Permissions.SURVEY],
        externalLink: false,
      },
      // {
      //   label: "Store KPI",
      //   requiredPermissions: [Permissions.STORE_KPI_REPORT],
      //   content: `reports/store-kpi`,
      //   externalLink: false,
      // },
      {
        label: 'Store Status Report',
        content: `/reports/store-status`,
        requiredPermissions: [Permissions.STORE_STATUS_REPORT],
        externalLink: false,
      },
      {
        label: 'Friend Referral Report',
        content: `/reports/friend-referral`,
        requiredPermissions: [Permissions.FRIEND_REFERRAL],
        externalLink: false,
      },
      {
        label: 'Social Media Campaign Report',
        content: `/reports/social-media-campaigns`,
        requiredPermissions: [Permissions.SOCIAL_MEDIA],
        externalLink: false,
      },
      {
        label: 'PWA Enrolment',
        content: `/reports/pwa-enrolment`,
        requiredPermissions: [Permissions.PERSONIFY],
        externalLink: false,
      },
      {
        label: 'Ecommerce',
        content: [
          {
            label: 'Product Views',
            content: `reports/ecommerce/product-views`,
            requiredPermissions: [Permissions.ECOMMERCE_REPORT],
            externalLink: false,
          },
          {
            label: 'Cart Items',
            content: `reports/ecommerce/cart-items`,
            requiredPermissions: [Permissions.ECOMMERCE_REPORT],
            externalLink: false,
          },
          {
            label: 'Landing Events',
            content: `reports/ecommerce/landing-events`,
            requiredPermissions: [Permissions.ECOMMERCE_REPORT],
            externalLink: false,
          },
          {
            label: 'Summary',
            content: `reports/ecommerce/summary`,
            requiredPermissions: [Permissions.ECOMMERCE_REPORT],
            externalLink: false,
          },
        ],
        requiredPermissions: [Permissions.ECOMMERCE_REPORT],
        externalLink: false,
      },
      {
        label: 'Ticket Desk',
        content: [
          {
            label: 'Ticket Export',
            content: `reports/ticket-desk/ticket-export`,
            requiredPermissions: [Permissions.TICKET_DESK_REPORT],
            externalLink: false,
          },
        ],
        requiredPermissions: [Permissions.TICKET_DESK_REPORT],
        externalLink: false,
      },
    ],
    externalLink: false,
  },
  {
    label: 'Import/Export',
    icon: ImportExport,
    requiredPermissions: [Permissions.DATA_MANAGE],
    content: [
      {
        label: 'Bill Uploads',
        content: `bill-upload`,
        requiredPermissions: [Permissions.DATA_MANAGE],
        externalLink: false,
      },
      {
        label: 'Upload Data',
        content: `/data-upload`,
        requiredPermissions: [Permissions.DATA_MANAGE],
        externalLink: false,
      },
      {
        label: 'Large Data Uploads',
        content: `${dashboardClassic}/parallel-upload`,
        requiredPermissions: [Permissions.DATA_MANAGE],
        externalLink: true,
      },
      {
        label: 'Download User Data',
        content: `${dashboardClassic}/user-data-download`,
        requiredPermissions: [Permissions.DATA_MANAGE],
        externalLink: true,
      },
    ],
    externalLink: false,
  },
  {
    requiredPermissions: [Permissions.SOCIAL_MEDIA_REWARDS],
    label: 'Social Connect',
    icon: SocialMediaReward,
    content: [
      {
        content: `${dashboardClassic}/social-media-engagements`,
        label: 'Rewards Summary',
        requiredPermissions: [Permissions.SOCIAL_MEDIA_REWARDS],
        externalLink: true,
      },
    ],
    externalLink: false,
  },
  {
    /*
     * CASA users, and a tenant's super admin (2026-11-03) — System stays CASA-only.
     *
     * Overview: a tenant lands on Data issues / Alert summary (HealthPage drops the internal tabs for
     * them; those read server-side scoped data). The fleet "Health" tab stays CASA-only: everything it
     * calls sits under `/admin/health/*`, which refuses non-internal users.
     *
     * Campaigns: its API is mounted under `users` so a tenant's own staff can watch their campaigns
     * run — reads pinned server-side to their own tenant, controls and fleet tabs kept internal (see
     * `api-server/src/routes/users/campaign-ops.ts`, CampaignOpsPage).
     */
    label: 'Health',
    icon: MonitorHeartIcon,
    superAdminOnly: true,
    requiredPermissions: [],
    content: [
      {
        label: 'Overview',
        content: `/health`,
        requiredPermissions: [],
        externalLink: false,
      },
      {
        // Run history first, then the live view and controls (was "Campaign Ops").
        label: 'Campaigns',
        content: `/health/campaigns`,
        requiredPermissions: [],
        externalLink: false,
      },
      /*
       * System — platform observability: machines, services, databases, Kafka, errors. Hidden unless the env
       * config sets systemPage (off in production and preprod). Under Health so all internal monitoring is one
       * menu; the page also refuses non-internal users (PageGuard is a no-op) and api-server /admin/ops/*
       * answers 403 to them.
       */
      ...(systemPage
        ? [
            {
              label: 'System',
              content: `/system`,
              requiredPermissions: [],
              externalLink: false,
              internalOnly: true,
            },
          ]
        : []),
    ],
    externalLink: false,
    isNew: true,
  },
  { label: 'Service Requests',
    icon: SupportAgentIcon,
    requiredPermissions: [],
    content: [
       {
        label: "Journey Requests",
        content: `/journeys/requests`,
        requiredPermissions: [Permissions.JOURNEY_BUILDER_MANAGE],
        externalLink: false,
      },
      {
        label: "Feature Requests",
        content: `/service-requests/feature-requests`,
        requiredPermissions: [Permissions.VIEW_FEATURE_REQUESTS],
        externalLink: false,
      },
    ],
    externalLink: false,
  },

  {
    requiredPermissions: [Permissions.DPDP_COMPLIANCE],
    label: 'DPDP Compliance',
    icon: PrivacyTipIcon,
    externalLink: false,
    isNew: true,
    content: [
      {
        content: `/dpdp-compliance/integration`,
        label: 'Platform Integration',
        requiredPermissions: [Permissions.DPDP_COMPLIANCE],
        externalLink: false,
      },
      {
        content: `/dpdp-compliance/breach-management`,
        label: 'Breach Management',
        requiredPermissions: [Permissions.DPDP_COMPLIANCE],
        externalLink: false,
      },
      {
        content: `/dpdp-compliance/compliance`,
        label: 'Compliance',
        requiredPermissions: [Permissions.DPDP_COMPLIANCE],
        externalLink: false,
      },
      {
        content: `/dpdp-compliance/reports`,
        label: 'Reports',
        requiredPermissions: [Permissions.DPDP_COMPLIANCE],
        externalLink: false,
      },
    ],
  },
  {
    label: 'Settings',
    icon: Settings,
    requiredPermissions: [Permissions.DATA_MANAGE],
    content: `/settings`,
    externalLink: false,
  },
];

export default navConfig;
